import { useCallback, useRef, useState } from 'react';
import { AlertType, Card, EmptyState, Icon, Icons, Spinner, TemplateRow } from '../../components';
import { MAX_CARD_WIDTH } from '../../constants';
import { useHandleApiResponse, useToast } from '../../hooks';
import {
  useDuplicateScorecardTemplateMutation,
  useGetScorecardTemplatesQuery,
  useUpdateScorecardTemplateMutation,
} from '../../services';
import { ComponentSize, ScorecardTemplate, ScorecardTemplateType, SemanticColor, SortingOrder, Tag } from '../../types';

const UPDATE_SCORECARD_ERROR_MSG = 'Failed to update scorecard';
const DUPLICATE_SCORECARD_ERROR_MSG = 'Failed to duplicate scorecard';

const ScorecardTemplatesTable = () => {
  // State to track which template is being duplicated
  const [duplicatingTemplateId, setDuplicatingTemplateId] = useState<string | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const handleApiResponse = useHandleApiResponse();
  const { showToast } = useToast();

  // APIs
  const { data: scorecardTemplates, isLoading } = useGetScorecardTemplatesQuery({
    sorting: {
      sortBy: 'updatedAt',
      sortOrder: SortingOrder.DESC,
    },
  });
  const [updateScorecardTemplate, { isLoading: isApplyingTags }] = useUpdateScorecardTemplateMutation();
  const [duplicateScorecardTemplate] = useDuplicateScorecardTemplateMutation();

  const scrollToTop = useCallback(() => {
    if (tableRef.current) {
      tableRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, []);

  // Handler to apply tags to a scorecard
  const handleApplyTags = useCallback(
    (template: ScorecardTemplate) => async (selectedTags: Tag[], setSelectedTags: (tags: Tag[]) => void) => {
      const selectedTagIds = selectedTags.map((tag) => tag.id);
      const templateTags = template.tags || [];

      // Do nothing if the selected tags are the same as the current tags.
      const shouldApplyTags =
        selectedTagIds.length !== templateTags.length ||
        !selectedTagIds.every((id) => templateTags.find((tag) => tag.id === id));
      if (!shouldApplyTags) {
        return;
      }

      try {
        const response = await updateScorecardTemplate({ id: template.id, tags: selectedTagIds });
        handleApiResponse({
          response,
          errorMsg: UPDATE_SCORECARD_ERROR_MSG,
          onError: () => setSelectedTags(templateTags),
        });
      } catch (error) {
        console.error(UPDATE_SCORECARD_ERROR_MSG, error);
        showToast({
          message: UPDATE_SCORECARD_ERROR_MSG,
          type: AlertType.ERROR,
        });
      }
    },
    [updateScorecardTemplate, handleApiResponse, showToast]
  );

  // Handles Scorecard Duplication
  const handleDuplicateScorecard = useCallback(
    async (scorecardId: string) => {
      try {
        setDuplicatingTemplateId(scorecardId);

        const response = await duplicateScorecardTemplate(scorecardId);
        handleApiResponse({
          response,
          errorMsg: DUPLICATE_SCORECARD_ERROR_MSG,
          onSuccess: scrollToTop,
        });
      } catch (error) {
        console.error(DUPLICATE_SCORECARD_ERROR_MSG, error);
        showToast({
          message: DUPLICATE_SCORECARD_ERROR_MSG,
          type: AlertType.ERROR,
        });
      } finally {
        setDuplicatingTemplateId(null);
      }
    },
    [duplicateScorecardTemplate, handleApiResponse, scrollToTop, showToast]
  );

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size={ComponentSize.MEDIUM} />
      </div>
    );
  }

  if (!scorecardTemplates?.length) {
    return <EmptyState description="No scorecard templates" icon={Icon.TALLY} />;
  }

  return (
    <Card ref={tableRef} maxWidth={MAX_CARD_WIDTH} className="mx-auto">
      {scorecardTemplates.map((scorecardTemplate) => (
        <TemplateRow
          key={scorecardTemplate.id}
          template={scorecardTemplate}
          isApplyingTags={isApplyingTags}
          isDuplicating={duplicatingTemplateId === scorecardTemplate.id}
          onApplyTags={handleApplyTags(scorecardTemplate)}
          onDuplicateTemplate={handleDuplicateScorecard}
          icon={
            scorecardTemplate.type === ScorecardTemplateType.AI ? (
              <Icons icon={Icon.WAND} color={SemanticColor.ACCENT} />
            ) : undefined
          }
        />
      ))}
    </Card>
  );
};

export default ScorecardTemplatesTable;
